
import { BidItem, CalculationResult, LaborRole, Quote, Invoice, InvoiceStatus } from '../types';

/**
 * Calculates the sell price for a material line item.
 * 
 * Formula: (UnitCost × Qty × PST × Markup)
 * Note: GST is NOT included in this calculation.
 * 
 * @param unitCost - The unit cost of the material
 * @param qty - The quantity of materials
 * @param pstFactor - Provincial Sales Tax factor (e.g., 1.07 for 7% PST)
 * @param markupFactor - Markup multiplier (e.g., 1.25 for 25% markup)
 * @returns The calculated sell price for the material line
 * 
 * @example
 * ```typescript
 * const sellPrice = calculateMaterialSell(100, 2, 1.07, 1.25);
 * // Returns: 100 * 2 * 1.07 * 1.25 = 267.5
 * ```
 */
export const calculateMaterialSell = (
  unitCost: number,
  qty: number,
  pstFactor: number,
  markupFactor: number
): number => {
  return unitCost * qty * pstFactor * markupFactor;
};

/**
 * Calculates comprehensive totals for a bid item including labor, materials, subcontractors, and profitability metrics.
 * 
 * This function processes all cost components of a bid item:
 * - Labor costs (both sell price and internal cost)
 * - Material costs (with PST and markup)
 * - Subcontractor costs
 * - Contingency calculations
 * - Gross profit and margin calculations
 * 
 * @param item - The bid item to calculate totals for
 * @param roles - Array of labor roles with rates and cost rates
 * @returns A CalculationResult object containing all calculated totals and metrics
 * 
 * @example
 * ```typescript
 * const result = calculateItemTotals(bidItem, laborRoles);
 * console.log(result.totalPreGst); // Total before GST
 * console.log(result.grossMarginPercent); // Profit margin percentage
 * ```
 */
export const calculateItemTotals = (item: BidItem, roles: LaborRole[]): CalculationResult => {
  // Defensive: Ensure arrays exist
  const laborSteps = item.laborSteps || [];
  const materialLines = item.materialLines || [];
  const subcontractLines = item.subcontractLines || [];

  // 1. Labor
  let totalManHours = 0;
  let totalLaborCost = 0; // Sell Price
  let totalLaborInternalCost = 0; // Internal Cost

  laborSteps.forEach(step => {
    const role = roles.find(r => r.id === step.roleId);
    const rate = role ? role.rate : 0;
    // Default to 60% of charge rate if costRate not set
    const costRate = role?.costRate ?? (rate * 0.6); 
    
    totalManHours += step.manHours;
    totalLaborCost += step.manHours * rate;
    totalLaborInternalCost += step.manHours * costRate;
  });

  // Derived Duration View
  const effectiveCrew = item.crewSize || 1;
  const effectiveShift = item.shiftHoursPerDay || 8;
  const calculatedDays = totalManHours / (effectiveCrew * effectiveShift);

  // 2. Material
  let totalMaterialSell = 0;
  let totalMaterialCost = 0;
  
  materialLines.forEach(mat => {
    totalMaterialSell += calculateMaterialSell(mat.unitCost, mat.qty, mat.pstFactor, mat.markupFactor);
    totalMaterialCost += (mat.unitCost * mat.qty * mat.pstFactor); // Cost includes PST usually for contractor
  });

  // 3. Subs
  let totalSubsSell = 0;
  let totalSubsCost = 0;
  subcontractLines.forEach(sub => {
    // Legacy fallback: Use totalCost if units aren't defined
    const qty = sub.qty || 1;
    const cost = sub.unitCost !== undefined ? sub.unitCost : sub.totalCost;
    const lineTotalCost = cost * qty;

    totalSubsSell += lineTotalCost * sub.markupFactor;
    totalSubsCost += lineTotalCost;
  });

  // 4. Aggregates
  const basePreGst = totalLaborCost + totalMaterialSell + totalSubsSell;
  const contingencyAmount = basePreGst * (item.contingencyPercent || 0);
  const totalPreGst = basePreGst + contingencyAmount;

  // 5. Profitability
  const totalCost = totalLaborInternalCost + totalMaterialCost + totalSubsCost;
  const grossProfit = totalPreGst - totalCost; // Contingency is pure profit if not used? We'll include it in revenue.
  const grossMarginPercent = totalPreGst > 0 ? (grossProfit / totalPreGst) : 0;

  return {
    totalManHours,
    totalLaborCost,
    totalLaborInternalCost,
    calculatedDays,
    totalMaterialSell,
    totalMaterialCost,
    totalSubsSell,
    totalSubsCost,
    basePreGst,
    contingencyAmount,
    totalPreGst,
    grossProfit,
    grossMarginPercent
  };
};

/**
 * Calculates comprehensive totals for an entire quote including all bid items, LOA, and final pricing.
 * 
 * This function aggregates all bid items, calculates quote-level totals, applies LOA (List of Assumptions),
 * and computes final pricing including GST.
 * 
 * @param quote - The quote object containing bid items and quote-level settings
 * @param roles - Array of labor roles with rates and cost rates
 * @returns An object containing quote totals, man hours, LOA cost, and final pricing breakdown
 * 
 * @example
 * ```typescript
 * const totals = calculateQuoteTotals(quote, laborRoles);
 * console.log(totals.grandTotal); // Final total including GST
 * console.log(totals.totalManHours); // Total man hours across all items
 * ```
 */
export const calculateQuoteTotals = (quote: Quote, roles: LaborRole[]) => {
  let quoteSubtotalPreGst = 0;
  let quoteManHours = 0;
  let totalLoaCost = 0; // Sell value of LOA
  let totalLaborCost = 0;
  let totalLaborInternalCost = 0;
  let totalMaterialSell = 0;
  let totalMaterialCost = 0;
  let totalSubsSell = 0;
  let totalSubsCost = 0;
  let basePreGst = 0;
  let contingencyAmount = 0;

  // Defensive check for bidItems
  const items = quote.bidItems || [];

  const itemResults = items.map(item => {
    const res = calculateItemTotals(item, roles);
    quoteSubtotalPreGst += res.totalPreGst;
    quoteManHours += res.totalManHours;
    totalLaborCost += res.totalLaborCost;
    totalLaborInternalCost += res.totalLaborInternalCost;
    totalMaterialSell += res.totalMaterialSell;
    totalMaterialCost += res.totalMaterialCost;
    totalSubsSell += res.totalSubsSell;
    totalSubsCost += res.totalSubsCost;
    basePreGst += res.basePreGst;
    contingencyAmount += res.contingencyAmount;
    return { itemId: item.id, ...res };
  });

  // Calculate LOA if enabled
  if (quote.enableLoa) {
    // Basic calculation: Total Man Hours / 10 hours (Standard shift?) or use average shift?
    // Let's use item-specific shifts to be accurate.
    let totalManDays = 0;
    items.forEach(item => {
        // Calculate man days for THIS item based on its specific shift configuration
        const shift = item.shiftHoursPerDay || 8; 
        // ManDays = ManHours / Shift
        totalManDays += (calculateItemTotals(item, roles).totalManHours / shift);
    });
    
    // Round up man days for safety
    totalManDays = Math.ceil(totalManDays); 
    const rate = quote.loaRate || 150;
    totalLoaCost = totalManDays * rate;
    
    quoteSubtotalPreGst += totalLoaCost;
  }

  const gstTotal = quoteSubtotalPreGst * quote.gstRate;
  const grandTotal = quoteSubtotalPreGst + gstTotal;
  const totalPreGst = quoteSubtotalPreGst;
  const totalCost = totalLaborInternalCost + totalMaterialCost + totalSubsCost;
  const grossProfit = totalPreGst - totalCost;
  const grossMarginPercent = totalPreGst > 0 ? (grossProfit / totalPreGst) : 0;

  return {
    itemResults,
    quoteSubtotalPreGst,
    gstTotal,
    grandTotal,
    quoteManHours,
    totalManHours: quoteManHours,
    totalLoaCost,
    totalLaborCost,
    totalLaborInternalCost,
    totalMaterialSell,
    totalMaterialCost,
    totalSubsSell,
    totalSubsCost,
    basePreGst,
    contingencyAmount,
    totalPreGst,
    grossProfit,
    grossMarginPercent
  };
};

// Helper: Calculate how much of each item has been billed previously
export const calculateBilledAmounts = (quote: Quote, invoices: Invoice[]) => {
  const billedPerItem: Record<string, number> = {};
  let totalBilledGlobal = 0;

  const validInvoices = invoices.filter(inv => inv.quoteId === quote.id && inv.status !== InvoiceStatus.DRAFT);

  validInvoices.forEach(inv => {
    totalBilledGlobal += inv.amount;
    
    if (inv.lines && inv.lines.length > 0) {
      inv.lines.forEach(line => {
        if (line.bidItemId) {
          billedPerItem[line.bidItemId] = (billedPerItem[line.bidItemId] || 0) + line.amount;
        }
      });
    }
  });

  return { billedPerItem, totalBilledGlobal };
};

export type { CalculationResult } from '../types';

export const checkOutageConstraints = (quote: Quote, roles: LaborRole[]) => {
  if (!quote.showOutageAnalysis) return []; // Skip if disabled

  const alerts: { groupId: string; groupName: string; allowed: number; planned: number; passed: boolean }[] = [];
  const groups = quote.outageGroups || [];
  const items = quote.bidItems || [];

  groups.forEach(group => {
    // Find all items in this group
    const groupItems = items.filter(i => i.outageGroupId === group.id);
    
    let totalPlannedDays = 0;
    groupItems.forEach(item => {
      const res = calculateItemTotals(item, roles);
      totalPlannedDays += res.calculatedDays;
    });

    alerts.push({
      groupId: group.id,
      groupName: group.name,
      allowed: group.allowedDays,
      planned: totalPlannedDays,
      passed: totalPlannedDays <= group.allowedDays
    });
  });

  return alerts;
};
